// Where the Team dock may sit (staff chat MVP, SR-14; Antonin's spec): 16 px from the window's right edge, or 16 px
// left of the Scrips Intelligence rail or panel while one is on screen, never over it, collapsed or open. It follows
// the rail as it opens, closes or resizes.
//
// Returns how far the dock's right edge sits from the window's right edge, in CSS px, WITHOUT the 16 px gap (the
// stylesheet adds it from the spacing token). 0 when nothing is to be avoided.
//
// Avoided: the encounter's Intelligence column (DS EncounterShell `encounter-intel-col`, open or folded to its strip),
// the Scrips Intelligence ContextRail (`aside[aria-label="Scrips Intelligence"]`), and anything a surface marks with
// `data-staff-chat-avoid` (and the catalog's `[data-intel-rail]`). As in Antonin's catalog source (`useRailClearance`), only
// a box that reaches the window's right edge (within 32) and its bottom (within 64) counts: the dock lives in that corner.
import { useEffect, useState } from 'react';

export const DOCK_AVOID_SELECTOR = [
  '[data-intel-rail]',
  '[data-slot="encounter-intel-col"]',
  'aside[aria-label="Scrips Intelligence"]',
  '[data-staff-chat-avoid]',
].join(', ');

/** How often, at most, a page change re-lists the avoided boxes (ms). */
export const LOOK_MS = 250;

/** The app's root density zoom (Signal DS `app.css`): rects are drawn at it, the dock's px are scaled by it. */
function rootZoom(): number {
  const z = Number.parseFloat(getComputedStyle(document.documentElement).zoom || '1');
  return Number.isFinite(z) && z > 0 ? z : 1;
}

/** The clearance now: the window's right edge to the left edge of the leftmost avoided box in the right half. */
export function measureDockClearance(): number {
  const viewport = window.innerWidth;
  let edge = Number.POSITIVE_INFINITY;
  for (const el of document.querySelectorAll(DOCK_AVOID_SELECTOR)) {
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) continue;
    if (r.right <= viewport - 32 || r.bottom <= window.innerHeight - 64 || r.left >= viewport) continue;
    edge = Math.min(edge, r.left);
  }
  if (!Number.isFinite(edge)) return 0;
  return Math.max(0, Math.round((viewport - edge) / rootZoom()));
}

/** The clearance, kept current: on mount, when an avoided box appears, goes, folds or resizes, and on window resize. */
export function useDockClearance(): number {
  const [clearance, setClearance] = useState(0);

  // Synchronises with the DOM outside React: the Intelligence rail is drawn by the routed page, not by the dock.
  // Cheap by design (the encounter page changes a lot): a page change only re-lists the avoided boxes (a selector query,
  // no layout read), at most once per LOOK_MS, and measures only when that list changed. The boxes themselves are
  // watched with a ResizeObserver (a rail that opens, folds or resizes), and the window with its resize event.
  useEffect(() => {
    let frame = 0;
    let lookTimer: ReturnType<typeof setTimeout> | undefined;
    const observed = new Set<Element>();
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measureSoon());
    function measureSoon() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setClearance(measureDockClearance()));
    }
    /** Re-lists the avoided boxes; measures when one appeared or went. */
    function look() {
      lookTimer = undefined;
      const now = new Set(document.querySelectorAll(DOCK_AVOID_SELECTOR));
      let changed = now.size !== observed.size;
      for (const el of observed)
        if (!now.has(el)) {
          resize?.unobserve(el);
          observed.delete(el);
          changed = true;
        }
      for (const el of now)
        if (!observed.has(el)) {
          resize?.observe(el);
          observed.add(el);
          changed = true;
        }
      if (changed) measureSoon();
    }
    const lookSoon = () => {
      if (lookTimer === undefined) lookTimer = setTimeout(look, LOOK_MS);
    };
    const mutations = new MutationObserver(lookSoon);
    mutations.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', measureSoon);
    look();
    measureSoon();
    return () => {
      cancelAnimationFrame(frame);
      if (lookTimer !== undefined) clearTimeout(lookTimer);
      mutations.disconnect();
      resize?.disconnect();
      window.removeEventListener('resize', measureSoon);
    };
  }, []);

  return clearance;
}
